import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * SVGArtifactPreview Component
 *
 * Renders SVG artifacts with:
 * - Toggle between code view and live preview
 * - Proper scaling and aspect ratio
 * - Syntax highlighted code view
 * - Support for SVG animations
 * - Copy button
 * - Zoom controls
 */
function SVGArtifactPreview({ content, title, isEditing = false, onContentChange }) {
  const [viewMode, setViewMode] = useState('preview'); // 'preview', 'code', or 'edit'
  const [copied, setCopied] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [bgColor, setBgColor] = useState('transparent'); // 'transparent', 'white', 'dark', 'checkered'
  const containerRef = useRef(null);
  const textareaRef = useRef(null);

  // Switch to edit view when editing starts
  useEffect(() => {
    if (isEditing) {
      setViewMode('edit');
    } else if (viewMode === 'edit') {
      setViewMode('preview');
    }
  }, [isEditing]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && viewMode === 'edit' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing, viewMode]);

  // Handle content changes in edit mode
  const handleChange = (e) => {
    if (onContentChange) {
      onContentChange(e.target.value);
    }
  };

  // Calculate line numbers for editor
  const lineCount = (content || '').split('\n').length;

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Zoom controls
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25));
  const handleZoomReset = () => setZoom(100);

  // Get background style based on selected option
  const getBackgroundStyle = () => {
    switch (bgColor) {
      case 'white':
        return 'bg-white';
      case 'dark':
        return 'bg-gray-800';
      case 'checkered':
        return 'bg-checkered';
      default:
        return 'bg-transparent';
    }
  };

  // Sanitize SVG content for safe rendering (DOS-261 / CN-006)
  // Uses DOMPurify with SVG profile — strips script, event handlers,
  // javascript: URIs, and foreignObject-based XSS vectors.
  const sanitizeSVG = (svgContent) => {
    if (!svgContent) return '';
    return DOMPurify.sanitize(svgContent, {
      USE_PROFILES: { svg: true, svgFilters: true },
      FORBID_TAGS: ['script', 'foreignObject'],
      FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onmouseout',
                    'onfocus', 'onblur', 'onkeydown', 'onkeyup', 'onkeypress'],
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar with view toggle */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        {/* View mode toggle */}
        <div className="flex items-center bg-gray-700 rounded-lg p-0.5">
          {isEditing && (
            <button
              onClick={() => setViewMode('edit')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'edit'
                  ? 'bg-gray-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </span>
            </button>
          )}
          <button
            onClick={() => setViewMode('preview')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              viewMode === 'preview'
                ? 'bg-gray-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Preview
            </span>
          </button>
          <button
            onClick={() => setViewMode('code')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              viewMode === 'code'
                ? 'bg-gray-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              Code
            </span>
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1">
          {/* Zoom controls (preview mode only) */}
          {viewMode === 'preview' && (
            <div className="flex items-center gap-1 mr-2 border-r border-gray-600 pr-2">
              <button
                onClick={handleZoomOut}
                className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded-sm transition-colors"
                title="Zoom out"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <button
                onClick={handleZoomReset}
                className="px-2 py-0.5 text-xs text-gray-400 hover:text-white hover:bg-gray-700 rounded-sm transition-colors"
                title="Reset zoom"
              >
                {zoom}%
              </button>
              <button
                onClick={handleZoomIn}
                className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded-sm transition-colors"
                title="Zoom in"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          )}

          {/* Background color selector (preview mode only) */}
          {viewMode === 'preview' && (
            <div className="flex items-center gap-1 mr-2 border-r border-gray-600 pr-2">
              <span className="text-xs text-gray-400 mr-1">BG:</span>
              <button
                onClick={() => setBgColor('transparent')}
                className={`w-5 h-5 rounded-sm border ${bgColor === 'transparent' ? 'border-blue-500' : 'border-gray-600'}`}
                style={{ background: 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%) 50% / 8px 8px' }}
                title="Transparent (checkered)"
              />
              <button
                onClick={() => setBgColor('white')}
                className={`w-5 h-5 rounded-sm border bg-white ${bgColor === 'white' ? 'border-blue-500' : 'border-gray-600'}`}
                title="White"
              />
              <button
                onClick={() => setBgColor('dark')}
                className={`w-5 h-5 rounded-sm border bg-gray-800 ${bgColor === 'dark' ? 'border-blue-500' : 'border-gray-600'}`}
                title="Dark"
              />
            </div>
          )}

          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-400 hover:text-white
              hover:bg-gray-700 rounded transition-colors"
            title={copied ? 'Copied!' : 'Copy SVG'}
          >
            {copied ? (
              <>
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-500">Copied!</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'edit' ? (
          // Split view - Editor on left, Preview on right
          <div className="flex h-full">
            {/* Code editor panel */}
            <div className="w-1/2 h-full overflow-auto bg-gray-900 border-r border-gray-700">
              <div className="flex h-full">
                {/* Line numbers column */}
                <div className="shrink-0 py-4 pl-4 pr-0 text-right text-gray-500 font-mono text-sm select-none"
                  style={{ minWidth: '3em', lineHeight: '1.6' }}
                >
                  {Array.from({ length: lineCount }, (_, i) => (
                    <div key={i + 1}>{i + 1}</div>
                  ))}
                </div>
                {/* Editable textarea */}
                <textarea
                  ref={textareaRef}
                  value={content || ''}
                  onChange={handleChange}
                  className="flex-1 bg-transparent text-gray-100 font-mono text-sm p-4 pl-4 resize-none focus:outline-hidden"
                  style={{
                    lineHeight: '1.6',
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    tabSize: 2,
                  }}
                  spellCheck={false}
                  placeholder="<!-- Enter your SVG here... -->"
                />
              </div>
            </div>
            {/* Live preview panel */}
            <div className="w-1/2 h-full p-2 bg-gray-100 dark:bg-gray-900 overflow-auto">
              <div
                className="min-h-full rounded-lg overflow-hidden shadow-xs border border-gray-200 dark:border-gray-700 flex items-center justify-center p-4"
                style={{ background: 'repeating-conic-gradient(#f0f0f0 0% 25%, #ffffff 0% 50%) 50% / 16px 16px' }}
              >
                <div
                  className="svg-container"
                  dangerouslySetInnerHTML={{ __html: sanitizeSVG(content) }}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                />
              </div>
            </div>
          </div>
        ) : viewMode === 'preview' ? (
          // Live preview with proper scaling
          <div
            ref={containerRef}
            className="h-full overflow-auto flex items-center justify-center p-4"
            style={bgColor === 'transparent' ? {
              background: 'repeating-conic-gradient(#e5e5e5 0% 25%, #ffffff 0% 50%) 50% / 16px 16px'
            } : undefined}
          >
            <div
              className={`${getBackgroundStyle()} rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 flex items-center justify-center`}
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'center center',
                transition: 'transform 0.2s ease',
              }}
            >
              <div
                className="svg-container"
                dangerouslySetInnerHTML={{ __html: sanitizeSVG(content) }}
                style={{
                  maxWidth: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            </div>
          </div>
        ) : (
          // Code view with syntax highlighting
          <div className="h-full overflow-auto bg-gray-900">
            <SyntaxHighlighter
              language="xml"
              style={oneDark}
              showLineNumbers={true}
              wrapLines={true}
              lineNumberStyle={{
                minWidth: '3em',
                paddingRight: '1em',
                textAlign: 'right',
                color: '#6b7280',
                userSelect: 'none',
              }}
              customStyle={{
                margin: 0,
                padding: '1rem',
                fontSize: '0.875rem',
                lineHeight: '1.6',
                background: 'transparent',
                minHeight: '100%',
              }}
              codeTagProps={{
                style: {
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                }
              }}
            >
              {content || '<!-- No SVG content -->'}
            </SyntaxHighlighter>
          </div>
        )}
      </div>

      {/* Preview info bar */}
      {viewMode === 'preview' && (
        <div className="px-4 py-2 bg-gray-800 border-t border-gray-700 text-xs text-gray-400 flex items-center gap-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>SVG vector graphic • Zoom: {zoom}% • Animations supported</span>
        </div>
      )}

      {/* Add CSS for checkered background and SVG container */}
      <style>{`
        .bg-checkered {
          background: repeating-conic-gradient(#e5e5e5 0% 25%, #ffffff 0% 50%) 50% / 16px 16px;
        }
        .svg-container svg {
          max-width: 100%;
          height: auto;
          display: block;
        }
      `}</style>
    </div>
  );
}

export default SVGArtifactPreview;
